// components/Modal/RejectRegistrationModal.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';
import Modal from './Modal';
import InputTextArea from '../Input/InputTextArea';
import { theme } from '../../theme';
import Typography from '../Typography';

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.sizing.sm};
`;

const AttendeeInfo = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  padding: ${({ theme }) => theme.sizing.sm};
  border-radius: ${({ theme }) => theme.radius.xs};
  margin-bottom: ${({ theme }) => theme.sizing.sm};
  gap: ${({ theme }) => theme.sizing.xs};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: ${({ theme }) => theme.radius.sms};
  
  &:last-child {
    padding-bottom: 0px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    gap: ${({ theme }) => theme.sizing.xxs};
    flex-basis: 100%;
  }
`;


const MessageSection = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.sizing.xs};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.sm};
  border-top: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  background: ${({ theme }) => theme.fill.defaultMain};
  align-self: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-wrap: wrap;
  }
`;

const RejectRegistrationModal = ({
  isOpen,
  onClose,
  attendee,
  onConfirm,
  isLoading = false
}) => {
  const { t } = useTranslation('manageEventAttendees');
  const [organizerMessage, setOrganizerMessage] = useState('');

  // Limpiar el mensaje solo cuando cambia el attendee (nueva sesión)
  React.useEffect(() => {
    setOrganizerMessage('');
  }, [attendee?._id]);

  if (!isOpen || !attendee) return null;

  const handleConfirm = () => {
    // Pasar attendeeId y message
    onConfirm(attendee._id, organizerMessage);
  };

  const handleClose = () => {
    // Limpiar el mensaje solo cuando el usuario cancela manualmente
    setOrganizerMessage('');
    onClose();
  };

  return (
    <Modal onClose={handleClose} title={t('rejectRegistrationModal.title')}>
      <ModalBody>
        <AttendeeInfo>
          <InfoRow>
            <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
              {t('rejectRegistrationModal.attendeeInfo.name')}
            </Typography>
            <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
              {attendee?.userId?.name || t('rejectRegistrationModal.attendeeInfo.defaultUser')} {attendee?.userId?.lastName || ''}
            </Typography>
          </InfoRow>
          <InfoRow>
            <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
              {t('rejectRegistrationModal.attendeeInfo.ticket')}
            </Typography>
            <Typography as="span" $variant="body-2-regular" color={theme.colors.defaultWeak}>
              {attendee?.ticketId?.name || t('rejectRegistrationModal.attendeeInfo.defaultTicket')}
            </Typography>
          </InfoRow>
        </AttendeeInfo>

        <MessageSection>
          <Label htmlFor="organizerMessage">
            <Typography as="span" $variant="body-2-medium" color={theme.colors.defaultStrong}>
              {t('rejectRegistrationModal.organizerMessage.label')}
            </Typography>
          </Label>
          <InputTextArea
            style={{ minHeight: '120px', resize: 'vertical' }}
            id="organizerMessage"
            value={organizerMessage}
            onChange={(e) => setOrganizerMessage(e.target.value)}
            placeholder={t('rejectRegistrationModal.organizerMessage.placeholder')}
          />
        </MessageSection>
      </ModalBody>

      <ModalFooter>
        <Button
          $variant="ghost"
          type="button"
          onClick={handleClose}
          disabled={isLoading}
          size="l"
          sizeTablet="l"
          sizeMobile="m"
        >
          {t('rejectRegistrationModal.buttons.cancel')}
        </Button>
        <Button
          $variant="defaultDanger"
          type="button"
          onClick={handleConfirm}
          disabled={isLoading}
          size="l"
          sizeTablet="l"
          sizeMobile="m"
        >
          {isLoading ? t('rejectRegistrationModal.buttons.processing') : t('rejectRegistrationModal.buttons.confirm')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RejectRegistrationModal;